import { FC, useCallback, useState } from "react"
import { Panel, Checkbox, Title, ExclusiveChoice } from "dash-styled"
import { routePaths } from "../../../../../../Routes"
import { ReactComponent as TriangleIcon } from "icons/lib/triangle.svg"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { useTagList } from "../../../../../../Hooks/useTagList"
import { TagsInputDropdown } from "./TagsInputDropdown"

interface AdvancedSettingsProps {
    appID: string
    className?: string
    queueOnSubmit: boolean
    tags: string[]
    strict: boolean
    onChangeQueueOnSubmit: () => void
    onChangeTags: (value: string[]) => void
    onChangeStrict: (value: boolean) => void
}

export const AdvancedSettings: FC<AdvancedSettingsProps> = ({
    appID,
    className,
    queueOnSubmit,
    tags,
    strict,
    onChangeQueueOnSubmit,
    onChangeTags,
    onChangeStrict
}) => {
    const [tagSearchKeyword, setTagSearchKeyword] = useState("")
    const [isExpanded, , , toggleExpanded] = useBoolean(false)
    const tagsFetch = useTagList(appID, tagSearchKeyword)
    const handleAddTag = useCallback(
        t => onChangeTags(tags.concat([t])),
        [onChangeTags, tags]
    )
    const handleRemoveTag = useCallback(
        t => onChangeTags(tags.filter(c => c !== t)),
        [onChangeTags, tags]
    )

    return (
        <Panel className={className}>
            <button
                onClick={toggleExpanded}
                className="w-full px-1"
                data-testid="advanced-settings"
            >
                <Title
                    component="h3"
                    variant="heading3"
                    className="flex items-baseline m-0"
                >
                    <TriangleIcon
                        className={`transition duration-200 ease-in-out mr-2 transform ${
                            isExpanded ? "rotate-90" : ""
                        }`}
                    />
                    Advanced settings
                </Title>
            </button>
            {isExpanded ? (
                <div>
                    <TagsInputDropdown
                        className="my-4"
                        allowAddNewTags={true}
                        tagsFetch={tagsFetch}
                        value={tags}
                        testId="add-data-tags"
                        onAdd={handleAddTag}
                        onRemove={handleRemoveTag}
                        onSearch={setTagSearchKeyword}
                    />
                    <div className="flex items-center">
                        <Checkbox
                            variant="modern"
                            className="text-rhino-900"
                            label="Park this batch (don't queue for processing yet)"
                            onChange={onChangeQueueOnSubmit}
                            isChecked={!queueOnSubmit}
                            testId="add-data-queue-checkbox"
                        />
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-pink-500 ml-1"
                            href={routePaths.docs(
                                "data-point-states#parking-your-data-points"
                            )}
                        >
                            More info
                        </a>
                    </div>
                    <div className="mt-4">
                        <Title
                            variant="heading4"
                            component="h4"
                            className="mb-2"
                        >
                            Error handling
                        </Title>
                        <ExclusiveChoice
                            withPanel={false}
                            choices={[
                                "Skip data points with errors",
                                "Reject all data point if one or more data points encounter errors"
                            ]}
                            selectedIndex={strict ? 1 : 0}
                            onClick={() => onChangeStrict(!strict)}
                        />
                    </div>
                </div>
            ) : null}
        </Panel>
    )
}
